export const getPrecisionOnCurrency = (currency) => {
  if (!currency) return 8;
  const lowerCurrency = currency.toLowerCase();
  const currencyPrecisionMap = {
    btc: 8,
    eth: 8,
    us$: 2,
    usd: 2,
    usdt: 2,
    usdc: 2,
    hkd: 2,
    jpy: 2,
    sgd: 2,
  };
  // Own-property lookup only: a bare index returns inherited members, so a currency of
  // "constructor" or "hasOwnProperty" yielded a truthy *function* instead of 8. Callers
  // interpolate this into a decimal-precision quantifier, so a non-number leaks out.
  return Object.prototype.hasOwnProperty.call(
    currencyPrecisionMap,
    lowerCurrency
  )
    ? currencyPrecisionMap[lowerCurrency]
    : 8;
};

// Amount-input guard: a plain non-negative decimal ("", "12", "12.", ".5") with at most
// `maxDecimals` fractional digits. Accepts exactly what `^\d*(\.\d{0,N})?$` did, but
// with a static pattern plus a scale check so currency-derived values never reach the
// RegExp compiler.
const DECIMAL_INPUT = /^\d*(\.\d*)?$/;

export const isDecimalWithinScale = (value, maxDecimals) => {
  const text = String(value);
  if (!DECIMAL_INPUT.test(text)) return false;
  const dot = text.indexOf('.');
  return (dot === -1 ? 0 : text.length - dot - 1) <= maxDecimals;
};

// Render a finite number as plain decimal text, expanding scientific notation
// (1e-7 → "0.0000001") so small crypto amounts truncate correctly.
const decimalTextFromNumber = (amount) => {
  if (!Number.isFinite(amount)) return null;

  const text = amount.toString();
  if (!/[eE]/.test(text)) return text;

  const [coefficient, exponentText] = text.toLowerCase().split('e');
  const exponent = Number(exponentText);
  if (!Number.isInteger(exponent)) return null;

  const sign =
    coefficient[0] === '-' || coefficient[0] === '+' ? coefficient[0] : '';
  const unsignedCoefficient = sign ? coefficient.slice(1) : coefficient;
  const [integerPart = '0', decimalPart = ''] = unsignedCoefficient.split('.');
  const digits = `${integerPart}${decimalPart}`;
  const decimalIndex = integerPart.length + exponent;

  if (decimalIndex <= 0) {
    return `${sign}0.${'0'.repeat(-decimalIndex)}${digits}`;
  }
  if (decimalIndex >= digits.length) {
    return `${sign}${digits}${'0'.repeat(decimalIndex - digits.length)}`;
  }
  return `${sign}${digits.slice(0, decimalIndex)}.${digits.slice(
    decimalIndex
  )}`;
};

const decimalStringForTruncation = (amount) => {
  if (typeof amount === 'number') {
    return decimalTextFromNumber(amount);
  }

  const text = String(amount).replace(/,/g, '').trim();
  if (!text) return null;
  return text;
};

const truncateDecimalText = (text, precision) => {
  const match = /^([+-]?)(\d*)(?:\.(\d*))?$/.exec(text);
  if (!match) return null;

  const [, sign, integerPart, decimalPart = ''] = match;
  if (decimalPart.length <= precision) return text;

  const safeIntegerPart = integerPart || '0';
  if (precision === 0) {
    return `${sign}${safeIntegerPart}`;
  }

  return `${sign}${safeIntegerPart}.${decimalPart.slice(0, precision)}`;
};

// Floor (truncate, never round half-up) a value to a fixed number of decimal
// places. Only touches values whose decimals EXCEED the scale, so a mid-typed
// "1.50" survives byte-identical. Returns the input unchanged when it is
// missing or unparseable, and preserves the input type (number in → number
// out, string in → string out). Mirrors backend CurrencyDecimalUtils.truncate
// (RoundingMode.DOWN).
export const truncateToScale = (amount, scale) => {
  if (amount === undefined || amount === null || amount === '') {
    return amount;
  }

  const decimalText = decimalStringForTruncation(amount);
  if (decimalText === null) return amount;

  const truncated = truncateDecimalText(decimalText, scale);
  if (truncated === null) return amount;

  return typeof amount === 'number' ? Number(truncated) : truncated;
};

// Floor a currency amount to its currency's scale — 2dp for fiat/stablecoins,
// 8dp otherwise. No-op without a currency.
export const truncateCurrencyAmount = (amount, currency) => {
  if (!currency) return amount;
  return truncateToScale(amount, getPrecisionOnCurrency(currency));
};

// Exchange rates floor to a fixed 4dp, rounded down (PB-1602 k4 — the sibling
// of the currency-amount floor above, on the same truncate-down rule).
export const EXCHANGE_RATE_DECIMAL_PLACES = 4;

export const truncateExchangeRate = (rate) =>
  truncateToScale(rate, EXCHANGE_RATE_DECIMAL_PLACES);
